import { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { compact } from 'lodash';
import {
  faBinoculars,
  faColumns,
  faHome,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faBooks } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { Button } from '@ebsco-ui/ebsco-ui';

import { FeatureFlags, Icon } from '@app/components';
import { useFeatureFlag, useHoldsAndCheckoutsPageConfig } from '@app/hooks';
import { sharedMessages } from '@app/translations';
import { Feature, Route, SetState } from '@app/constants';

import { NavigationDropdown } from './NavigationDropdown';

import css from './NavigationPanel.module.scss';

interface NavigationPanelProps {
  isMobile?: boolean;
  setShowDrawer?: SetState<boolean>;
}

export const NavigationPanel: FC<NavigationPanelProps> = ({
  isMobile,
  setShowDrawer,
}) => {
  const { $t } = useIntl();
  const { isActive: isHoldsAndCheckoutsFlagActive } = useFeatureFlag({
    name: Feature.holdsAndCheckouts,
    isActiveIfNotFound: true,
  });
  const {
    holdsAndCheckoutsRoute,
    selectConfiguredOption: selectConfiguredHoldsAndCheckoutsOption,
  } = useHoldsAndCheckoutsPageConfig();

  const handleCloseMobileMenu = () => setShowDrawer && setShowDrawer(false);

  const browseDropdownLinks = useMemo(
    () => [
      {
        flags: [{ name: Feature.courseReserves }],
        label: $t({
          defaultMessage: 'Course reserves',
          id: 'courseReserves.title',
        }),
        href: Route.courseReserves,
      },
      {
        flags: [{ name: Feature.callNumbers }],
        label: $t({
          defaultMessage: 'Call numbers',
          id: 'navigation.callNumbers',
        }),
        href: Route.callNumbers,
      },
    ],
    [$t]
  );

  const dashboardDropdownLinks = useMemo(
    () =>
      compact([
        {
          label: $t(sharedMessages.projects),
          href: Route.projects,
        },
        {
          label: $t(sharedMessages.saved),
          href: Route.saved,
        },
        {
          label: $t(sharedMessages.recentActivity),
          href: Route.recentActivity,
        },
        isHoldsAndCheckoutsFlagActive && {
          label: selectConfiguredHoldsAndCheckoutsOption(
            $t(sharedMessages.requestsAndLoans),
            $t(sharedMessages.holdsAndCheckouts)
          ),
          href: holdsAndCheckoutsRoute,
        },
      ]),
    [
      $t,
      holdsAndCheckoutsRoute,
      isHoldsAndCheckoutsFlagActive,
      selectConfiguredHoldsAndCheckoutsOption,
    ]
  );

  const renderNavigationLink = ({
    to,
    icon,
    label,
  }: {
    to: string;
    icon: IconDefinition;
    label: string;
  }) => (
    <li>
      <NavLink
        to={to}
        className={({ isActive }) => cn(css.link, { [css.isActive]: isActive })}
        onClick={handleCloseMobileMenu}
      >
        <Icon icon={icon}>{label}</Icon>
      </NavLink>
    </li>
  );

  return (
    <nav
      className={css.navigation}
      aria-label={$t({
        id: 'navigation.main',
        defaultMessage: 'Main',
      })}
    >
      {isMobile && (
        <div className={css.closeButtonWrapper}>
          <Button
            styleType="flat-icon"
            className={css.closeButton}
            aria-label={$t(sharedMessages.close)}
            onClick={handleCloseMobileMenu}
          >
            <FontAwesomeIcon className="fa-lg" icon={faTimes} />
          </Button>
        </div>
      )}
      <ul className={css.links}>
        {renderNavigationLink({
          to: Route.search,
          icon: faHome,
          label: $t({
            id: 'navigation.catalogHome',
            defaultMessage: 'Catalog home',
          }),
        })}
        <FeatureFlags
          flagsToMatch={[{ name: Feature.LCDashboard }]}
          renderOn={() => (
            <NavigationDropdown
              name="dashboard"
              icon={faColumns}
              links={dashboardDropdownLinks}
              isMobile={isMobile}
              setShowDrawer={setShowDrawer}
            />
          )}
          renderOff={() => (
            <FeatureFlags
              flagsToMatch={[
                {
                  name: Feature.holdsAndCheckouts,
                  isActiveIfNotFound: true,
                },
              ]}
            >
              {renderNavigationLink({
                to: holdsAndCheckoutsRoute,
                icon: faBooks,
                label: $t({
                  id: 'navigation.myBookshelf',
                  defaultMessage: 'My bookshelf',
                }),
              })}
            </FeatureFlags>
          )}
        />
        <NavigationDropdown
          name="browse"
          icon={faBinoculars}
          links={browseDropdownLinks}
          flags={[
            { name: Feature.callNumbers },
            { name: Feature.courseReserves },
          ]}
          isMobile={isMobile}
          setShowDrawer={setShowDrawer}
        />
      </ul>
    </nav>
  );
};
